// Shared postMessage protocol for the MFE (micro-frontend) token handoff between the main
// app (parent window) and an embedded MFE (iframe). Both the main app and the MFE host app
// import these definitions so the message shapes stay in sync.

/**
 * Posted by the MFE bootstrap (running inside the iframe) to its parent once it is mounted
 * and ready to receive the read-only token. The parent does not send the token until it has
 * received this, which avoids a race with the iframe's script load.
 */
export interface MfeReadyMessage {
  type: "datafabriq-mfe-ready";
}

/**
 * Posted by the parent (main app) to the MFE iframe, handing over the short-lived read-only
 * app JWT. The MFE then POSTs this token to `/api/v1/auth/mfe-session` to establish a
 * host-only read-only session on its own origin.
 */
export interface MfeTokenMessage {
  type: "datafabriq-mfe-token";
  token: string;
}

/**
 * Posted by the parent (main app) to the MFE iframe to switch the embedded view without reloading
 * the iframe. The iframe is loaded once; subsequent view changes are sent as navigate messages so
 * the ops app's React tree, read-only session, and data cache stay warm across navigations. The
 * path is a route only (no token), so it carries nothing sensitive.
 */
export interface MfeNavigateMessage {
  type: "datafabriq-mfe-navigate";
  path: string;
}

/**
 * Posted by the parent (main app) to the MFE iframe to forward a SignalR data-change notification.
 * The iframe runs its own SignalR connection, but it is best-effort and can silently fail to
 * establish inside the frame; forwarding the parent's notifications gives the embedded app a
 * reliable second channel so its cached data (e.g. dashboard widget/view code) refreshes without a
 * full page reload. Carries the changed Cosmos container name and the client instance that
 * originated the change (so the iframe can ignore echoes of its own writes) — never anything
 * sensitive.
 */
export interface MfeDataChangedMessage {
  type: "datafabriq-mfe-data-changed";
  containerName: string;
  sourceClientInstanceId: string | null;
}

export const mfeReadyMessageType = "datafabriq-mfe-ready" as const;
export const mfeTokenMessageType = "datafabriq-mfe-token" as const;
export const mfeNavigateMessageType = "datafabriq-mfe-navigate" as const;
export const mfeDataChangedMessageType = "datafabriq-mfe-data-changed" as const;

export function isMfeReadyMessage(data: unknown): data is MfeReadyMessage {
  return typeof data === "object" && data !== null && (data as { type?: unknown }).type === mfeReadyMessageType;
}

export function isMfeTokenMessage(data: unknown): data is MfeTokenMessage {
  if (typeof data !== "object" || data === null) {
    return false;
  }

  const message = data as { type?: unknown; token?: unknown };

  return message.type === mfeTokenMessageType && typeof message.token === "string";
}

export function isMfeNavigateMessage(data: unknown): data is MfeNavigateMessage {
  if (typeof data !== "object" || data === null) {
    return false;
  }

  const message = data as { type?: unknown; path?: unknown };

  return message.type === mfeNavigateMessageType && typeof message.path === "string";
}

export function isMfeDataChangedMessage(data: unknown): data is MfeDataChangedMessage {
  if (typeof data !== "object" || data === null) {
    return false;
  }

  const message = data as { type?: unknown; containerName?: unknown };

  return message.type === mfeDataChangedMessageType && typeof message.containerName === "string";
}
